import {
  Edit,
  SimpleForm,
  TextInput,
  ArrayInput,
  SimpleFormIterator,
  required,
  useDataProvider,
  NumberInput,
} from 'react-admin';
import { Card, CardContent, Typography, Stack } from '@mui/material';
import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import ItemSelectInput from '../../components/ItemSelectInput.tsx';
import UnitInput from '../../components/UnitInput.tsx';
import { heIL } from '@mui/material/locale';
import { Button } from 'react-admin';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import EditIcon from '@mui/icons-material/Edit';
import { useNotify, useRedirect, useRefresh } from 'react-admin';

const BomEdit = () => {
  const dataProvider = useDataProvider();
  const navigate = useNavigate();
  const { id } = useParams();
  const refresh = useRefresh();
  const notify = useNotify();
  const redirect = useRedirect();
  // 统一请求 FinishedGood 产品
  const [finishedGoods, setFinishedGoods] = useState<any[]>([]);
  const [rawMaterials, setRawMaterials] = useState<any[]>([]);
  const [packagingMaterials, setPackagingMaterials] = useState<any[]>([]);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<any>(null);

  useEffect(() => {
    setLoading(true);

    // 并行请求 FinishedGood 和 RawMaterial（假设接口都能用getList和filter）
    Promise.all([
      dataProvider.getList('bom/getFinishedGoodsSelectInput', {
        pagination: { page: 1, perPage: 10000 },
        sort: { field: 'productId', order: 'ASC' },
        filter: { type: 'FinishedGood' },
      }),
      dataProvider.getList('bom/getFinishedGoodsSelectInput', {
        pagination: { page: 1, perPage: 10000 },
        sort: { field: 'productId', order: 'ASC' },
        filter: { type: 'RawMaterial' },
      }),
      dataProvider.getList('bom/getFinishedGoodsSelectInput', {
        pagination: { page: 1, perPage: 10000 },
        sort: { field: 'productId', order: 'ASC' },
        filter: { type: 'PackagingMaterial' },
      }),
    ])
      .then(([finishedGoodsRes, rawMaterialsRes, packagingMaterialsRes]) => {
        setFinishedGoods(finishedGoodsRes.data);
        setRawMaterials(rawMaterialsRes.data);
        setPackagingMaterials(packagingMaterialsRes.data);

        setLoading(false);
      })
      .catch(e => {
        setError(e);
        setLoading(false);
      });
  }, [dataProvider]);

  if (loading) return <span>Loading...</span>;
  if (error) return <span>Error loading product options</span>;

  return (
    <div style={{ padding: '24px' }}>
      <Card elevation={3} sx={{ borderRadius: 3 }}>
        <CardContent>
          {/* 顶部按钮区 */}
          <Stack direction="row" spacing={2} sx={{ mb: 2, margin: 2 }}>
            <Button
              label="List"
              onClick={() => navigate('/bom')}
              startIcon={<ArrowBackIcon />}
            />
            <Button
              label="Chart"
              onClick={() => navigate(`/bom/${id}/chart`)}
              startIcon={<EditIcon />}
              color="primary"
            />
          </Stack>
          <Typography sx={{ marginLeft: 2.5 }} variant="h6" gutterBottom>
            Edit BOM
          </Typography>

          <Edit
            key={id}
            title=" "
            transform={(data) => {
              const bomId = data.bomId ?? data.id;

              const toId = (m: any) =>
                typeof m === "object" ? (m.productId ?? m.id) : m;

              // 先处理 items
              const items = (data.items ?? []).map((item: any) => {
                const id = toId(item.material ?? item.productId);
                // 直接取 material.unit
                const unit = item.material?.unit ?? item.unit ?? null;
                return {
                  bomId,
                  productId: id,
                  quantity: Number(item.quantity) || 0,
                  unit,
                  proportion: Number(item.proportion) || 0,
                };
              });

              // 把 packagingItems 合并到 items
              const packagingAsItems = (data.packagingItems ?? []).map((item: any) => {
                const id = toId(item.material ?? item.productId);
                // 直接取 material.unit
                const unit = item.material?.unit ?? item.unit ?? null;
                return {
                  bomId,
                  productId: id,
                  quantity: Number(item.quantity) || 0,
                  unit,
                  proportion: 0, // 包材一般没有配比，可以固定 0
                };
              });

              return {
                ...data,
                bomId,
                productId: toId(data.productId),
                items: [...items, ...packagingAsItems],
              };
            }}
            onSuccess={() => {
              notify('Save Success', { type: 'success' });
              redirect('/bom');
              setTimeout(() => refresh(), 10); // 跳转后刷新

            }}
          >
            <SimpleForm sx={{ width: '100%' }}>
              <TextInput
                source="id"
                label="BOM id"
                sx={{ width: '100%' }}
                validate={required()}
              />
              {/* <TextInput sx={{ width: '100%' }} source="version" label="Version" fullWidth /> */}

              {/* FinishedGood 选项从父组件传入 */}
              <ItemSelectInput
                source="productId"
                choices={finishedGoods}
                sx={{
                  '& .MuiAutocomplete-inputRoot': {
                    minHeight: 50, // 你想要的高度
                    height: 50,
                    alignItems: 'center',
                  },
                  // 兼容 SelectInput
                  '& .MuiInputBase-root': {
                    minHeight: 50,
                    height: 50,
                    alignItems: 'center',
                  },
                }}
              />
              <TextInput className="custom-text-input" source="blendSize" label='blend(kg)' />
              {/* <TextInput className="custom-text-input" source="lossRate" label='lossRate(%)' /> */}

              <ArrayInput source="items" label="Raw Materials">
                <SimpleFormIterator
                  inline
                  fullWidth
                  sx={{
                    '& .RaSimpleFormIterator-line': {
                      mt: 2,
                    },
                    '& .RaSimpleFormIterator-field': {
                      mt: 2,
                    },
                    '& .RaSimpleFormIterator-action': {
                      mt: 2,
                    },
                  }}
                >
                  {/* RawMaterial 选项从父组件传入 */}
                  <ItemSelectInput
                    showSearch
                    source="material"
                    choices={rawMaterials}
                    sx={{
                      '& .MuiAutocomplete-inputRoot': {
                        minHeight: 50, // 你想要的高度
                        height: 50,
                        alignItems: 'center',
                      },
                      // 兼容 SelectInput
                      '& .MuiInputBase-root': {
                        minHeight: 50,
                        height: 50,
                        alignItems: 'center',
                      },
                    }}
                  />
                  <TextInput sx={{ mt: 1 }} source="quantity" label="quantity" fullWidth />
                  {/* <UnitInput source="material.unit" arrayName="items" /> */}
                  {/* <TextInput sx={{ mt: 1 }} label="proportion(%)" source="proportion" /> */}
                </SimpleFormIterator>
              </ArrayInput>
              {/* 新增包装材料 ArrayInput */}
              <ArrayInput source="packagingItems" label="Packaging Materials">
                <SimpleFormIterator inline fullWidth sx={{
                  '& .RaSimpleFormIterator-line': { mt: 2 },
                  '& .RaSimpleFormIterator-field': { mt: 2 },
                  '& .RaSimpleFormIterator-action': { mt: 2 },
                }}>
                  <ItemSelectInput
                    sx={{
                      '& .MuiAutocomplete-inputRoot': {
                        minHeight: 50,
                        height: 50,
                        alignItems: 'center',
                      },
                      '& .MuiInputBase-root': {
                        minHeight: 50,
                        height: 50,
                        alignItems: 'center',
                      },
                    }}
                    choices={packagingMaterials}
                    showSearch
                    source="material"
                  />
                  <TextInput sx={{ mt: 1 }} source="quantity" label="quantity" />
                  {/* <UnitInput source="material.unit" arrayName="packagingItems" /> */}
                </SimpleFormIterator>
              </ArrayInput>
              <TextInput source="notes" label="notes" multiline fullWidth minRows={3} />
            </SimpleForm>
          </Edit>
        </CardContent>
      </Card>
    </div>
  );
};

export default BomEdit;
